import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Field, inputClass } from './BackgroundFields';
import WebCreatorTextEditor from './WebCreatorTextEditor';
import {
  RESTAURANT_SECTION_VARIANT_LABELS,
  RESTAURANT_SECTION_VARIANT_ORDER,
  DEFAULT_RESTAURANT_EXEMPLE_PROPS,
} from '../utils/restaurantExempleDefaults';

const ZONE_ORDER = RESTAURANT_SECTION_VARIANT_ORDER;

function HtmlField({ label, value, onChange, siteTypography }) {
  return (
    <Field label={label}>
      <WebCreatorTextEditor
        value={value || '<p><br></p>'}
        onChange={onChange}
        siteTypography={siteTypography}
        inheritSiteFont={false}
      />
    </Field>
  );
}

function TextField({ label, value, onChange, multiline = false }) {
  return (
    <Field label={label}>
      {multiline ? (
        <textarea
          className={inputClass}
          rows={3}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className={inputClass}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Field>
  );
}

function ImageUrlField({ label, value, onChange }) {
  return (
    <>
      <TextField label={label} value={value} onChange={onChange} />
      {value ? (
        <div className="rounded-lg border border-border overflow-hidden bg-muted/30 p-2">
          <img src={value} alt="" className="max-h-40 w-full object-cover rounded" />
        </div>
      ) : null}
    </>
  );
}

function DishCardsEditor({ dishCards, onChange }) {
  const list = Array.isArray(dishCards) ? dishCards : [];
  const update = (index, patch) => {
    onChange(list.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };
  const add = () => {
    onChange([
      ...list,
      {
        metaLeft: '—',
        metaRight: 'À découvrir',
        title: 'Nouveau plat',
        description: '',
        image: '',
        offset: false,
      },
    ]);
  };
  const remove = (index) => {
    onChange(list.filter((_, i) => i !== index));
  };
  return (
    <div className="space-y-4">
      {list.map((dish, index) => (
        <div key={index} className="rounded-lg border border-border p-3 space-y-2 bg-muted/5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-foreground">
              Plat {index + 1}
            </span>
            <button
              type="button"
              className="text-destructive text-xs hover:underline"
              onClick={() => remove(index)}
            >
              Supprimer
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <TextField
              label="Ligne gauche"
              value={dish.metaLeft}
              onChange={(v) => update(index, { metaLeft: v })}
            />
            <TextField
              label="Ligne droite"
              value={dish.metaRight}
              onChange={(v) => update(index, { metaRight: v })}
            />
          </div>
          <TextField
            label="Titre"
            value={dish.title}
            onChange={(v) => update(index, { title: v })}
          />
          <TextField
            label="Description"
            value={dish.description}
            onChange={(v) => update(index, { description: v })}
            multiline
          />
          <ImageUrlField
            label="Image (URL)"
            value={dish.image}
            onChange={(v) => update(index, { image: v })}
          />
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(dish.offset)}
              onChange={(e) => update(index, { offset: e.target.checked })}
            />
            Décalage vertical (colonne du milieu)
          </label>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add}>
        <Icon name="Plus" size={14} className="mr-1" />
        Ajouter un plat
      </Button>
    </div>
  );
}

function GalleryEditor({ images, alts, onChangeImages, onChangeAlts }) {
  const urls = Array.isArray(images) ? images : [];
  const altList = Array.isArray(alts) ? alts : [];
  const setUrl = (i, url) => {
    const next = [...urls];
    next[i] = url;
    onChangeImages(next);
  };
  const setAlt = (i, alt) => {
    const next = [...altList];
    while (next.length < urls.length) next.push('');
    next[i] = alt;
    onChangeAlts(next);
  };
  const add = () => onChangeImages([...urls, '']);
  const remove = (i) => {
    onChangeImages(urls.filter((_, idx) => idx !== i));
    onChangeAlts(altList.filter((_, idx) => idx !== i));
  };
  return (
    <div className="space-y-3">
      {urls.map((url, i) => (
        <div key={i} className="rounded-lg border border-border p-3 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs font-semibold">Photo {i + 1}</span>
            <button type="button" className="text-xs text-destructive" onClick={() => remove(i)}>
              Retirer
            </button>
          </div>
          <ImageUrlField label="URL" value={url} onChange={(v) => setUrl(i, v)} />
          <TextField label="Texte alternatif" value={altList[i] || ''} onChange={(v) => setAlt(i, v)} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add}>
        Ajouter une photo
      </Button>
    </div>
  );
}

function ZoneFields({ zone, props, setProp, siteTypography }) {
  switch (zone) {
    case 'chrome':
      return (
        <>
          <TextField
            label="Bandeau annonce"
            value={props.announcement}
            onChange={(v) => setProp('announcement', v)}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Marque — ligne 1"
              value={props.brandLine1}
              onChange={(v) => setProp('brandLine1', v)}
            />
            <TextField
              label="Marque — ligne 2 (italique)"
              value={props.brandLine2}
              onChange={(v) => setProp('brandLine2', v)}
            />
          </div>
          <TextField
            label="Libellé menu « La carte » (click & collect)"
            value={props.menuCartLabel}
            onChange={(v) => setProp('menuCartLabel', v)}
          />
        </>
      );
    case 'hero':
      return (
        <>
          <ImageUrlField
            label="Image de fond"
            value={props.heroImage}
            onChange={(v) => setProp('heroImage', v)}
          />
          <TextField
            label="Surtitre"
            value={props.heroEyebrow}
            onChange={(v) => setProp('heroEyebrow', v)}
          />
          <HtmlField
            label="Titre principal"
            value={props.heroTitleHtml}
            onChange={(html) => setProp('heroTitleHtml', html)}
            siteTypography={siteTypography}
          />
          <HtmlField
            label="Texte d’accroche"
            value={props.heroCopy}
            onChange={(html) => setProp('heroCopy', html)}
            siteTypography={siteTypography}
          />
          <TextField
            label="Lien secondaire (ancre #carte)"
            value={props.heroDiscoverLabel}
            onChange={(v) => setProp('heroDiscoverLabel', v)}
          />
        </>
      );
    case 'intro':
      return (
        <>
          <ImageUrlField
            label="Photo"
            value={props.introImage}
            onChange={(v) => setProp('introImage', v)}
          />
          <TextField
            label="Surtitre"
            value={props.introEyebrow}
            onChange={(v) => setProp('introEyebrow', v)}
          />
          <HtmlField
            label="Titre"
            value={props.introTitleHtml}
            onChange={(html) => setProp('introTitleHtml', html)}
            siteTypography={siteTypography}
          />
          <TextField
            label="Paragraphe"
            value={props.introBody}
            onChange={(v) => setProp('introBody', v)}
            multiline
          />
        </>
      );
    case 'seasonal':
      return (
        <>
          <ImageUrlField
            label="Image"
            value={props.seasonalImage}
            onChange={(v) => setProp('seasonalImage', v)}
          />
          <TextField
            label="Surtitre"
            value={props.seasonalEyebrow}
            onChange={(v) => setProp('seasonalEyebrow', v)}
          />
          <HtmlField
            label="Titre"
            value={props.seasonalTitleHtml}
            onChange={(html) => setProp('seasonalTitleHtml', html)}
            siteTypography={siteTypography}
          />
          <TextField
            label="Texte"
            value={props.seasonalText}
            onChange={(v) => setProp('seasonalText', v)}
            multiline
          />
          <TextField
            label="Tags (séparés par | )"
            value={props.seasonalTags}
            onChange={(v) => setProp('seasonalTags', v)}
          />
        </>
      );
    case 'dishes':
      return (
        <>
          <TextField
            label="Surtitre"
            value={props.menuEyebrow}
            onChange={(v) => setProp('menuEyebrow', v)}
          />
          <HtmlField
            label="Titre"
            value={props.menuTitleHtml}
            onChange={(html) => setProp('menuTitleHtml', html)}
            siteTypography={siteTypography}
          />
          <HtmlField
            label="Note à droite"
            value={props.menuNote}
            onChange={(html) => setProp('menuNote', html)}
            siteTypography={siteTypography}
          />
          <DishCardsEditor
            dishCards={props.dishCards}
            onChange={(next) => setProp('dishCards', next)}
          />
          <TextField
            label="Mention légale sous la grille"
            value={props.menuDisclaimer}
            onChange={(v) => setProp('menuDisclaimer', v)}
            multiline
          />
        </>
      );
    case 'quote':
      return (
        <>
          <HtmlField
            label="Citation"
            value={props.quoteHtml}
            onChange={(html) => setProp('quoteHtml', html)}
            siteTypography={siteTypography}
          />
          <TextField
            label="Signature"
            value={props.quoteBy}
            onChange={(v) => setProp('quoteBy', v)}
          />
        </>
      );
    case 'gallery':
      return (
        <>
          <TextField
            label="Surtitre"
            value={props.galleryEyebrow}
            onChange={(v) => setProp('galleryEyebrow', v)}
          />
          <HtmlField
            label="Titre"
            value={props.galleryTitleHtml}
            onChange={(html) => setProp('galleryTitleHtml', html)}
            siteTypography={siteTypography}
          />
          <HtmlField
            label="Note à droite"
            value={props.galleryNote}
            onChange={(html) => setProp('galleryNote', html)}
            siteTypography={siteTypography}
          />
          <GalleryEditor
            images={props.galleryImages}
            alts={props.galleryImageAlts}
            onChangeImages={(next) => setProp('galleryImages', next)}
            onChangeAlts={(next) => setProp('galleryImageAlts', next)}
          />
        </>
      );
    case 'reservation':
      return (
        <>
          <p className="text-xs text-muted-foreground rounded-lg bg-muted/30 p-3">
            Les créneaux et disponibilités se règlent dans{' '}
            <strong>Administration → Réservation</strong>. Ici : textes affichés à côté du
            formulaire.
          </p>
          <TextField
            label="Surtitre"
            value={props.reservationEyebrow}
            onChange={(v) => setProp('reservationEyebrow', v)}
          />
          <HtmlField
            label="Titre"
            value={props.reservationTitleHtml}
            onChange={(html) => setProp('reservationTitleHtml', html)}
            siteTypography={siteTypography}
          />
          <TextField
            label="Intro"
            value={props.reservationLead}
            onChange={(v) => setProp('reservationLead', v)}
            multiline
          />
          <TextField
            label="Encadré — titre"
            value={props.reservationNoteTitle}
            onChange={(v) => setProp('reservationNoteTitle', v)}
          />
          <TextField
            label="Encadré — texte"
            value={props.reservationNoteText}
            onChange={(v) => setProp('reservationNoteText', v)}
            multiline
          />
          <TextField
            label="Formulaire — surtitre"
            value={props.bookingKicker}
            onChange={(v) => setProp('bookingKicker', v)}
          />
          <TextField
            label="Formulaire — titre"
            value={props.bookingTitle}
            onChange={(v) => setProp('bookingTitle', v)}
          />
        </>
      );
    case 'practical':
      return (
        <>
          <TextField
            label="Surtitre"
            value={props.practicalEyebrow}
            onChange={(v) => setProp('practicalEyebrow', v)}
          />
          <HtmlField
            label="Titre"
            value={props.practicalTitleHtml}
            onChange={(html) => setProp('practicalTitleHtml', html)}
            siteTypography={siteTypography}
          />
          <HtmlField
            label="Adresse (HTML)"
            value={props.addressHtml}
            onChange={(html) => setProp('addressHtml', html)}
            siteTypography={siteTypography}
          />
          <HtmlField
            label="Horaires (HTML)"
            value={props.hoursHtml}
            onChange={(html) => setProp('hoursHtml', html)}
            siteTypography={siteTypography}
          />
          <HtmlField
            label="Contact (HTML)"
            value={props.contactHtml}
            onChange={(html) => setProp('contactHtml', html)}
            siteTypography={siteTypography}
          />
          <TextField
            label="E-mail (lien mailto)"
            value={props.contactEmail}
            onChange={(v) => setProp('contactEmail', v)}
          />
        </>
      );
    case 'footer':
      return (
        <>
          <TextField
            label="Phrase d’accroche"
            value={props.footerTagline}
            onChange={(v) => setProp('footerTagline', v)}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Marque — ligne 1"
              value={props.brandLine1}
              onChange={(v) => setProp('brandLine1', v)}
            />
            <TextField
              label="Marque — ligne 2"
              value={props.brandLine2}
              onChange={(v) => setProp('brandLine2', v)}
            />
          </div>
        </>
      );
    default:
      return null;
  }
}

export default function RestaurantContentSettingsFields({
  props,
  setProp,
  setProps,
  siteTypography,
  variant = null,
  componentType = 'restaurantPage',
}) {
  const zones = useMemo(() => {
    if (variant && RESTAURANT_SECTION_VARIANT_ORDER.includes(variant)) {
      return [variant];
    }
    return ZONE_ORDER;
  }, [variant]);

  const [activeZone, setActiveZone] = useState(zones[0] || 'chrome');

  useEffect(() => {
    if (!zones.includes(activeZone)) setActiveZone(zones[0] || 'chrome');
  }, [zones, activeZone]);

  const showNav = zones.length > 1;

  return (
    <div className="space-y-4">
      {componentType === 'restaurantSection' ? (
        <Field label="Zone de la page">
          <select
            className={inputClass}
            value={props.variant || variant || 'hero'}
            onChange={(e) => setProp('variant', e.target.value)}
          >
            {RESTAURANT_SECTION_VARIANT_ORDER.map((v) => (
              <option key={v} value={v}>
                {RESTAURANT_SECTION_VARIANT_LABELS[v]}
              </option>
            ))}
          </select>
        </Field>
      ) : (
        <p className="text-xs text-muted-foreground">
          Modèle La Table des Jardins — modifiez chaque zone. Le rendu public reste identique au
          design d’origine.
        </p>
      )}

      {showNav ? (
        <div className="flex flex-wrap gap-1.5 border-b border-border pb-3">
          {zones.map((z) => (
            <button
              key={z}
              type="button"
              className={`text-[11px] px-2.5 py-1.5 rounded-md border transition-colors ${
                activeZone === z
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background border-border hover:bg-muted'
              }`}
              onClick={() => setActiveZone(z)}
            >
              {RESTAURANT_SECTION_VARIANT_LABELS[z] || z}
            </button>
          ))}
        </div>
      ) : null}

      <ZoneFields
        zone={activeZone}
        props={{ ...DEFAULT_RESTAURANT_EXEMPLE_PROPS, ...props }}
        setProp={setProp}
        siteTypography={siteTypography}
      />

    </div>
  );
}
